import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '@hooks/useTranslation';
import Accordion from '@components/ui/Accordion/Accordion';
import { Button } from '@components/ui';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { getFormConfigById } from '@/services/formService';
import { patchFileComponents } from '@/lib/formioFileProvider';
import { hasRequiredFields } from '@utils/formioHelpers';
import useFormioResize from '@hooks/useFormioResize';
import CheckIcon from '@assets/icons/CheckIcon';
import type { FormProps, FormSource } from '@formio/react/lib/components/Form';
import styles from './MandatoryFormRenderer.module.scss';

// Lazy-load Formio Form
const FormioForm = lazy(() =>
  import('@formio/react').then((mod) => ({ default: mod.Form })),
);

// ============================================
// TYPES
// ============================================

interface FormioInstance {
  checkValidity: (data: Record<string, unknown>, dirty: boolean) => boolean;
}

export interface MandatoryFormRendererProps {
  formConfigId: string;
  formName: string;
  initialSaved?: boolean;
  /** Pre-existing form data to restore (e.g., from a previous step visit). */
  initialData?: Record<string, unknown>;
  onSave: (formConfigId: string, data: Record<string, unknown>) => void;
  /** Reports whether this form needs explicit save before parent can proceed.
   *  `true` = form has required fields, needs save.
   *  `false` = no required fields, auto-passes. */
  onRequiredCheck: (formConfigId: string, needsSave: boolean) => void;
}

// ============================================
// COMPONENT
// ============================================

function MandatoryFormRenderer({
  formConfigId,
  formName,
  initialSaved = false,
  initialData,
  onSave,
  onRequiredCheck,
}: MandatoryFormRendererProps) {
  const { t } = useTranslation();
  const [isSaved, setIsSaved] = useState(initialSaved);
  const [isExpanded, setIsExpanded] = useState(!initialSaved);
  const formRef = useRef<Record<string, unknown> | null>(initialData ?? null);

  const submission = useMemo(
    () => (initialData ? { data: initialData } as unknown as Record<string, unknown> : undefined),
    [initialData],
  );
  const instanceRef = useRef<FormioInstance | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: formConfig, isLoading, isError, error } = useQuery({
    queryKey: ['formConfig', formConfigId],
    queryFn: () => getFormConfigById(formConfigId),
  });
  const isForbidden = isForbiddenError(error);

  useFormioResize(formConfig?.formConfig);

  // Patch file components to use our file-management-service
  const patchedFormConfig = useMemo(() => {
    if (!formConfig?.formConfig) return null;
    return patchFileComponents(
      JSON.parse(JSON.stringify(formConfig.formConfig)) as Record<string, unknown>,
    );
  }, [formConfig?.formConfig]);

  // Report to parent whether this form has required fields (needs explicit save)
  useEffect(() => {
    if (formConfig?.formConfig) {
      const hasRequired = hasRequiredFields(formConfig.formConfig);
      onRequiredCheck(formConfigId, hasRequired);
    }
  }, [formConfig, formConfigId, onRequiredCheck]);

  const handleChange = useCallback(
    (sub: { data: Record<string, unknown> }) => {
      formRef.current = sub.data;
    },
    [],
  );

  const handleFormReady = useCallback((instance: unknown) => {
    instanceRef.current = instance as FormioInstance;
  }, []);

  const handleSave = useCallback(() => {
    // Fallback to empty object if onChange hasn't fired yet (no user interaction)
    const data = formRef.current ?? {};

    // Validate via Formio instance if available
    const instance = instanceRef.current;
    if (instance) {
      const isValid = instance.checkValidity(data, true);
      if (!isValid) {
        // Scroll to the first invalid field within this form's container
        const errorEl = containerRef.current?.querySelector('.has-error, .formio-error-wrapper');
        if (errorEl) {
          errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
    }

    onSave(formConfigId, data);
    setIsSaved(true);
    setIsExpanded(false);
  }, [formConfigId, onSave]);

  const savedBadge = isSaved ? (
    <span className={styles.badgeSaved}>
      <CheckIcon /> {t('K_BADGE_SAVED', 'Saved')}
    </span>
  ) : undefined;

  return (
    <Accordion
      title={formName}
      variant="filled"
      whiteBody
      expanded={isExpanded}
      onExpandedChange={setIsExpanded}
      action={savedBadge}
    >
      <div ref={containerRef} className={styles.formContainer}>
        {isLoading && <div className={styles.loading}>{t('K_LOADING_FORM', 'Loading form...')}</div>}
        {isForbidden && <Forbidden variant="inline" hideAction />}
        {isError && !isForbidden && <div className={styles.error}>{t('K_FAILED_LOAD_FORM', 'Failed to load form.')}</div>}
        {patchedFormConfig && !isForbidden && (
          <>
            <div className={styles.formRoot}>
              <Suspense fallback={<div className={styles.loading}>{t('K_LOADING_FORM', 'Loading form...')}</div>}>
                <FormioForm
                  src={patchedFormConfig as unknown as FormSource}
                  submission={submission as FormProps['submission']}
                  onChange={handleChange}
                  formReady={handleFormReady}
                />
              </Suspense>
            </div>
            <div className={styles.actions}>
              <Button
                variant="filled"
                size="sm"
                onClick={handleSave}
              >
                {t('K_SAVE', 'Save')}
              </Button>
            </div>
          </>
        )}
      </div>
    </Accordion>
  );
}

export default MandatoryFormRenderer;
